/**
 * Binary layouts shared by the main thread and simulation worker.
 *
 * All multi-byte values are little-endian. Atomic header/record fields are
 * native-endian Int32 values (all supported browser targets are little-endian).
 * Float telemetry uses Float64 so a write/read does not unnecessarily quantize
 * simulation state. Offsets are bytes from the start of their named section.
 */

export const I32_BYTES = 4;
export const F64_BYTES = 8;

export const MAX_TARGETS = 32;
export const MAX_TRACKS = 64;
/** One length byte followed by at most 63 UTF-8 bytes. */
export const MAX_TARGET_ID_BYTES = 64;

export const TARGET_RECORD_BYTES = MAX_TARGET_ID_BYTES + 3 * F64_BYTES;
export const TRACK_RECORD_BYTES = 2 * I32_BYTES + 6 * F64_BYTES;

/** Telemetry header: atomic sequence (0) and active-slot index (4). */
export const TelemetryHeaderOffset = Object.freeze({
  sequence: 0,
  activeSlot: 4,
});
export const TELEMETRY_HEADER_BYTES = 2 * I32_BYTES;

/** Target record: length-prefixed UTF-8 id (0), ENU position xyz (64). */
export const TargetRecordOffset = Object.freeze({
  id: 0,
  pos: MAX_TARGET_ID_BYTES,
});

/** Track record: id (0), state enum (4), ENU position xyz (8), ENU velocity xyz (32). */
export const TrackRecordOffset = Object.freeze({
  id: 0,
  state: 4,
  pos: 8,
  vel: 32,
});

/**
 * One telemetry slot. Scalar/vector values occupy the following byte ranges:
 *
 * - time [0,8), scout pos [8,32), quaternion [32,64), velocity [64,88)
 * - scout angular velocity [88,112), battery [112,120), motor RPM [120,152)
 * - FC flags [152,156), FC mode [156,160), active setpoint [160,164)
 * - companion state [164,168), selected track id [168,172)
 * - unstable [172,176), target count [176,180), track count [180,184)
 * - target records [184,3000), track records [3000,6584)
 * - checksum [6584,6588), alignment padding [6588,6592)
 *
 * FC flag bits are armed=0, saturated=1, crashed=2, manualLatched=3.
 * Enum codes are array indices in these orders:
 * FC mode = idle, acro, angle, rate, crashed; active setpoint = idle, manual,
 * goto, loiter; companion = IDLE, SLEWING, ON_STATION, TARGET_LOST, RTL;
 * track state = tentative, confirmed, coasting, dropped. A selected-track id
 * of -1 represents null.
 */
export const TelemetrySlotOffset = Object.freeze({
  time: 0,
  scoutPos: 8,
  scoutQuat: 32,
  scoutVel: 64,
  scoutAngVel: 88,
  batteryV: 112,
  motorRpm: 120,
  fcFlags: 152,
  fcMode: 156,
  activeSetpointKind: 160,
  companionState: 164,
  selectedTrackId: 168,
  unstable: 172,
  targetCount: 176,
  trackCount: 180,
  targets: 184,
  tracks: 184 + MAX_TARGETS * TARGET_RECORD_BYTES,
  checksum: 184 + MAX_TARGETS * TARGET_RECORD_BYTES + MAX_TRACKS * TRACK_RECORD_BYTES,
  padding: 184 + MAX_TARGETS * TARGET_RECORD_BYTES + MAX_TRACKS * TRACK_RECORD_BYTES + 4,
});

export const TELEMETRY_SLOT_BYTES = TelemetrySlotOffset.padding + I32_BYTES;
/** Header plus two complete telemetry slots. */
export const TELEMETRY_BUFFER_SIZE = TELEMETRY_HEADER_BYTES + 2 * TELEMETRY_SLOT_BYTES;

export const COMMAND_RING_CAPACITY = 64;
export const COMMAND_TUNING_SLOTS = 8;

/** Command header: atomic monotonic producer head (0), consumer tail (4). */
export const CommandHeaderOffset = Object.freeze({
  head: 0,
  tail: 4,
});
export const COMMAND_HEADER_BYTES = 2 * I32_BYTES;

/**
 * Command record:
 * - atomic per-record sequence [0,4), alignment padding [4,8)
 * - roll/pitch/yaw/throttle Float64 channels [8,40)
 * - mode enum [40,44), discrete flags [44,48)
 * - eight Float64 tuning slots [48,112)
 *
 * Flag bits are arm=0, disarm=1, reset=2, releaseManual=3.
 */
export const CommandRecordOffset = Object.freeze({
  sequence: 0,
  padding: 4,
  channels: 8,
  mode: 40,
  flags: 44,
  tuning: 48,
});
export const COMMAND_RECORD_BYTES = CommandRecordOffset.tuning + COMMAND_TUNING_SLOTS * F64_BYTES;
/** Header plus 64 command records. */
export const COMMAND_BUFFER_SIZE =
  COMMAND_HEADER_BYTES + COMMAND_RING_CAPACITY * COMMAND_RECORD_BYTES;

export type StateBuffer = ArrayBuffer | SharedArrayBuffer;

export const telemetrySlotByteOffset = (slot: number): number => {
  if (slot !== 0 && slot !== 1) throw new RangeError('telemetry slot must be 0 or 1');
  return TELEMETRY_HEADER_BYTES + slot * TELEMETRY_SLOT_BYTES;
};

export const commandRecordByteOffset = (slot: number): number => {
  if (!Number.isInteger(slot) || slot < 0 || slot >= COMMAND_RING_CAPACITY) {
    throw new RangeError(`command slot must be in [0, ${COMMAND_RING_CAPACITY})`);
  }
  return COMMAND_HEADER_BYTES + slot * COMMAND_RECORD_BYTES;
};
